import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { InlineSpinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Phone, Calendar, MessageSquare, MapPin, ShieldCheck, Users, ArrowRight, Sparkles, LifeBuoy,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SCHED_BASE = BACKEND;

function initials(name) {
  return (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}

function Avatar({ m, size = "md" }) {
  const dim = size === "lg" ? "w-20 h-20 text-xl" : "w-14 h-14 text-base";
  if (m.photo_url) {
    return (
      <img src={`${BACKEND}${m.photo_url}`} alt={`${m.name} headshot`}
        className={`${dim} rounded-2xl object-cover shrink-0 bg-slate-100`} />
    );
  }
  return (
    <span className={`${dim} rounded-2xl bg-[#013A5A] text-white flex items-center justify-center font-bold shrink-0`} aria-hidden="true">
      {initials(m.name)}
    </span>
  );
}

function ContactActions({ m, onMessage, compact }) {
  return (
    <div className={`flex flex-wrap gap-2 ${compact ? "mt-2" : "mt-3"}`} data-testid={`team-actions-${m.id}`}>
      {m.message_enabled && (
        <Button size="sm" onClick={() => onMessage(m)} data-testid={`team-message-${m.id}`}
          className="bg-[#0D9488] hover:bg-[#0b7d73] text-white border-0">
          <MessageSquare className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />Message
        </Button>
      )}
      {m.schedule_url && (
        <a href={`${SCHED_BASE}${m.schedule_url}`} target="_blank" rel="noopener noreferrer"
          data-testid={`team-schedule-${m.id}`}
          className="inline-flex items-center rounded-md border border-[#006090]/40 bg-white px-3 py-1.5 text-sm font-medium text-[#006090] hover:bg-[#006090]/5">
          <Calendar className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />Schedule Time
        </a>
      )}
      {m.phone && (
        <a href={`tel:${m.phone.replace(/[^0-9+]/g, "")}`} data-testid={`team-call-${m.id}`}
          className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
          <Phone className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />Call
        </a>
      )}
    </div>
  );
}

function MemberCard({ m, onMessage }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`team-card-${m.id}`}>
      <div className="flex gap-4">
        <Avatar m={m} />
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold text-slate-900 leading-tight">
            {m.name}{m.credentials ? <span className="text-slate-400 font-medium">, {m.credentials}</span> : null}
          </p>
          <p className="text-sm text-[#006090] font-medium">{m.title}</p>
          {m.role_summary && <p className="text-sm text-slate-600 mt-1.5">{m.role_summary}</p>}
        </div>
      </div>
      {(m.how_i_can_help?.length > 0 || m.specialties?.length > 0) && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">How I can help</p>
          <ul className="mt-1.5 grid sm:grid-cols-2 gap-x-4 gap-y-1">
            {(m.how_i_can_help?.length ? m.how_i_can_help : m.specialties).slice(0, 6).map((s, i) => (
              <li key={i} className="text-sm text-slate-700 flex items-start gap-1.5">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-[#0D9488] shrink-0" aria-hidden="true" />{s}
              </li>
            ))}
          </ul>
        </div>
      )}
      <ContactActions m={m} onMessage={onMessage} />
    </div>
  );
}

const CAT_ICON = {
  district_leadership: ShieldCheck, agency_recruiting: Users, operations: MapPin,
  training: Sparkles, coaching: Sparkles, marketing: Sparkles, life: LifeBuoy, financial: LifeBuoy,
};

export default function MyTeam({ onMessage, internalDistrictPicker = false }) {
  const [data, setData] = useState(null);
  const [districtId, setDistrictId] = useState("");

  useEffect(() => {
    setData(null);
    const url = districtId ? `/team/my-team?district_id=${districtId}` : "/team/my-team";
    api.get(url).then((r) => setData(r.data)).catch(() => setData({ categories: [], all_members: [] }));
  }, [districtId]);

  const message = (m) => {
    if (onMessage) onMessage(m);
    else window.dispatchEvent(new Event("tr-open-support"));
  };

  if (data === null) return <div className="py-16 flex justify-center"><InlineSpinner /></div>;

  const rec = data.recruiter_card;
  const hasAny = (data.all_members || []).length > 0 || rec || (data.support_contacts || []).length > 0;

  return (
    <div className="max-w-5xl" data-testid="my-team-page">
      <div className="rounded-3xl bg-gradient-to-br from-[#013A5A] to-[#025b86] text-white p-8 sm:p-10 mb-8">
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Meet Your Support Team</h1>
        <p className="text-white/85 mt-3 max-w-2xl">
          You're not building your agency alone. Your district team is here to help with recruiting,
          licensing, training, business development, operations, marketing and agency growth.
        </p>
        {data.district && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm" data-testid="team-district">
            <MapPin className="w-4 h-4" aria-hidden="true" />{data.district.name}
          </div>
        )}
      </div>

      {!hasAny && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500" data-testid="team-empty">
          Your support team will appear here soon.
        </div>
      )}

      {/* Featured recruiter */}
      {rec ? (
        <section className="mb-10" data-testid="team-recruiter-section" id="my-recruiter">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#0D9488] mb-3">Your Agency Recruiter</p>
          <div className="rounded-2xl border-2 border-[#0D9488]/40 bg-[#0D9488]/5 p-6" data-testid="team-recruiter-card">
            <div className="flex gap-5">
              <Avatar m={rec} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="font-display text-xl font-bold text-slate-900">{rec.name}</p>
                <p className="text-sm text-[#006090] font-medium">{rec.title}</p>
                {data.recruiter_areas?.length > 0 && <p className="text-xs text-slate-600 mt-1" data-testid="team-recruiter-areas">Also your contact for: {data.recruiter_areas.join(", ")}</p>}
                {rec.role_summary && <p className="text-sm text-slate-600 mt-2">{rec.role_summary}</p>}
                {rec.how_i_can_help?.length > 0 && (
                  <ul className="mt-3 flex flex-wrap gap-2">
                    {rec.how_i_can_help.slice(0, 5).map((s, i) => (
                      <li key={i}><Badge variant="outline" className="border-[#0D9488]/40 text-slate-600">{s}</Badge></li>
                    ))}
                  </ul>
                )}
                <ContactActions m={rec} onMessage={message} />
              </div>
            </div>
          </div>
        </section>
      ) : data.district && (
        <section className="mb-10" data-testid="team-recruiter-empty">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="font-semibold text-slate-900">Your recruiting team is here to help.</p>
            <p className="text-sm text-slate-600 mt-1">
              Reach out any time and the right person on your district team will connect with you.
            </p>
            <Button size="sm" onClick={() => message({})} data-testid="team-recruiter-empty-message"
              className="mt-3 bg-[#0D9488] hover:bg-[#0b7d73] text-white border-0">
              <MessageSquare className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />Message the team
            </Button>
          </div>
        </section>
      )}

      {/* Assigned support (Support Assignments) */}
      {data.support_contacts?.length > 0 && (
        <section className="mb-10" data-testid="team-support-assignments">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-1">Your Support Contacts</h2>
          <p className="text-sm text-slate-500 mb-3">The people assigned to help you, and the areas each one handles.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.support_contacts.map(({ member: m, areas }) => (
              <div key={m.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex gap-3" data-testid={`team-support-${m.id}`}>
                <Avatar m={m} />
                <div className="min-w-0 flex-1">
                  <p className="font-display font-bold text-slate-900 leading-tight">{m.name}</p>
                  {m.title && <p className="text-xs text-[#006090]">{m.title}</p>}
                  <ul className="mt-1.5 flex flex-wrap gap-1.5">{areas.map((a) => <li key={a}><Badge variant="outline" className="border-[#0D9488]/40 text-slate-600">{a}</Badge></li>)}</ul>
                  <ContactActions m={m} onMessage={message} compact />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Who should I contact? */}
      {data.contact_topics?.length > 0 && hasAny && (
        <section className="mb-10" data-testid="team-contact-topics">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Who should I contact?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {data.contact_topics.map((t) => {
              const target = t.recruiter ? rec
                : (data.categories.find((c) => c.key === t.category)?.members || [])[0];
              if (!target) return null;
              return (
                <button key={t.key} data-testid={`team-topic-${t.key}`}
                  onClick={() => { const el = document.getElementById(`team-card-${target.id}`) || document.getElementById("my-recruiter"); el?.scrollIntoView({ behavior: "smooth", block: "center" }); el?.classList.add("ring-2", "ring-[#0D9488]"); setTimeout(() => el?.classList.remove("ring-2", "ring-[#0D9488]"), 1800); }}
                  className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-[#006090] hover:shadow-sm transition-all">
                  <p className="text-sm font-semibold text-slate-900">{t.label}</p>
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">{target.name}<ArrowRight className="w-3 h-3" aria-hidden="true" /></p>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Leadership */}
      {data.leadership?.length > 0 && (
        <section className="mb-10" data-testid="team-leadership">
          <h2 className="font-display text-lg font-bold text-slate-900 mb-3">District Leadership</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.leadership.map((m) => <MemberCard key={m.id} m={m} onMessage={message} />)}
          </div>
        </section>
      )}

      {/* Categories */}
      {data.categories?.filter((c) => c.key !== "district_leadership").map((c) => {
        const Icon = CAT_ICON[c.key] || Users;
        return (
          <section key={c.key} className="mb-10" data-testid={`team-cat-${c.key}`}>
            <h2 className="font-display text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
              <Icon className="w-5 h-5 text-[#006090]" aria-hidden="true" />{c.label}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {c.members.map((m) => <MemberCard key={m.id + c.key} m={m} onMessage={message} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}
